import React, {useMemo, useState} from 'react'
import Project from "../../../models/Project";
import InfoTooltip from "../../../components/InfoTooltip";
import {useNavigate, useParams} from "react-router-dom";
import {useProjectForm} from "../../../providers/ProjectFormProvider";
import {useAuth} from "../../../providers/AuthProvider";
import {useProject} from "../../../providers/ClientProjectProvider";

const BriefDetailsTab = (props) => {
    const {projectId} = useParams();
    const {
        isLoading,
        project,
    } = useProject();
    const {
        formModel,
        isProjectUpdating,
        onFormReset,
        onFormChange,
        onFormSubmit
    } = useProjectForm();
    const navigate = useNavigate();
    const {auth} = useAuth();
    const [activeStep, setActiveStep] = useState(null);

    const formSteps = useMemo(() => {
        if (isLoading || !projectId || !project) return [];

        return TB.filter(project.currentSteps(), step => step.StepSection === Project.ProjectSections.SectionProfile ||
            step.StepSection === Project.ProjectSections.SectionType
        );
    }, [projectId, isLoading, project,isProjectUpdating]);

    const isDisabled = isProjectUpdating || isLoading || props.isDisabled

    let canEditScope = auth?.canEditProjectAndPositions(project);

    const hasFormChange = useMemo(() => {
        if (!activeStep || !formModel) return false;

        const getStepProps = TB.find(formSteps,['slug', activeStep]).ValidationMethodName;

        const prevProps = project[getStepProps]?.();
        const editingProps = formModel[getStepProps]?.();

        return prevProps && editingProps && !TB.isEqual(prevProps, editingProps)
    }, [activeStep, project, formModel, isProjectUpdating]);

    const checkPromptForFormChange = async() => {
        if (hasFormChange) {

            if (!confirm("You have unsaved changes on this form. Are you sure you want to discard your changes and leave?")) {
                return true;
            }
            await clearProjectForm();

        }
        return false;
    }
    const clearProjectForm = async() => {
        if (!isDisabled) {
            await onFormReset();
            return setActiveStep(null)
        }
    }

    const editProjectStep = async(step) => {
        console.log('editProjectStep', step)
        if (isDisabled) return;

        const stillEditingStep = await checkPromptForFormChange();

        if (!stillEditingStep) {
            return setActiveStep(step.slug)

        }
    }

    if (!formSteps.length) return null;

    return formSteps.map((Step, si) => {

        let fields = Step.reviewMapping(project, auth);

        let isActive = activeStep === Step.slug;
        if (!fields || !Object.keys(fields).length) {
            return null;
        }

        return TB.map(fields, (Fields, stepLabel) => {

            return (
                <div key={`ds-${stepLabel}`} className="review-item">

                    <p className="review-item-title">
                        {stepLabel}
                    </p>
                    {isActive ? (
                        <div className="edit-item-section">
                            <Step
                                {...props}
                                isDisabled={isDisabled}
                                formOnly={true}
                                project={formModel}
                                onChange={(props) => {
                                    if (!isDisabled) {
                                        console.log('onChange', props)
                                        return onFormChange(props)
                                    }
                                }}/>

                            <div className="edit-actions">
                                <button
                                    type="button"
                                    className="btn btn-outline-dark"
                                    disabled={isDisabled}
                                    onClick={clearProjectForm}>
                                    Cancel
                                </button>

                                <button
                                    className="btn btn-primary"
                                    disabled={isDisabled || !hasFormChange}
                                    onClick={async () => {
                                        let validatedProps = await Step.hasCompleted(formModel, project);
                                        // console.log('ProjectBrief @ onSubmit -> validated', validatedProps);

                                        if (!validatedProps) {
                                            return TB.event("CreateProject@Submit");
                                        }

                                        let didUpdate = await onFormSubmit(validatedProps);

                                        if (didUpdate) {
                                            TB.toast('Project Brief Updated')
                                            return clearProjectForm();
                                        }
                                    }}
                                >
                                    Update
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="review-item-section">
                            <div className="review-item-info">

                                {(!TB.isObject(Fields) || TB.isElement(Fields)) ? (
                                        typeof Fields === 'string' ? (
                                            <p className="review-item-label">
                                                {Fields}
                                            </p>

                                        ) : Fields
                                    )
                                    : TB.map(Fields, (propValue, propLabel) => {
                                        let key = `detail-steps-${si}-fields-${propLabel}`;

                                        if (TB.isElement(propValue)) {
                                            return (
                                                <div key={key} className="flex flex-col gap-3">
                                                    <p key={key} className="review-item-label">
                                                        {propLabel}:
                                                    </p>
                                                    {propValue}
                                                </div>
                                            )
                                        }
                                        if (!propValue || TB.isObject(propValue)) return (
                                            <div key={key} className="review-item-label"/>
                                        )

                                        return (
                                            <p key={key} className="review-item-label">
                                                {propLabel}:
                                                <span className="review-item-value">
                                                    {propValue}
                                                </span>
                                            </p>
                                        );
                                    })}
                            </div>

                            <div className={"review-item-action"}>
                                {
                                    (project.canEditScope() && !project.is_on_hold) ? (
                                        <button
                                            disabled={isDisabled}
                                            onClick={() => {
                                                if (!canEditScope) {
                                                    return TB.showPermissionError();
                                                }
                                                return editProjectStep(Step)
                                            }}
                                            className={TB.mapClass('btn btn-link', {
                                                'disabled-edit-link': !canEditScope,
                                            })}
                                        >
                                            Edit

                                            {!canEditScope && (
                                                <i className="fa fa-lock ml-1"/>
                                            )}
                                        </button>
                                    ) : (
                                        <div className="disabled-edit-link">
                                            <p className="mb-0">
                                                <span className="mr-2">Not Editable</span>
                                                <InfoTooltip placement="top" type="light">
                                                    <span className="text-black">
                                                        {project.is_on_hold ?
                                                            'This section is not editable while the project is on hold.' :
                                                            'This section is not editable at this phase of the project.'
                                                        }
                                                    </span>
                                                </InfoTooltip>
                                            </p>
                                        </div>
                                    )
                                }
                            </div>
                        </div>
                    )}

                </div>
            )
        });
    });
}

export default BriefDetailsTab;